import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { ROUTES } from '../constants/app.constants';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';

/** Silencia el toast global de error para una petición concreta. */
export const SKIP_ERROR_TOAST = new HttpContextToken(() => false);

/**
 * Contexto para peticiones de fondo (sondeos, precargas): el error se maneja
 * en la vista y no interrumpe con un toast. El 401 se sigue atendiendo.
 */
export function silentRequest(): HttpContext {
  return new HttpContext().set(SKIP_ERROR_TOAST, true);
}

/**
 * Manejo centralizado de errores HTTP.
 * - 401: la sesión ya no se pudo recuperar (el `authInterceptor` va por dentro
 *   y ya intentó renovar el token), así que se limpia y se manda a login.
 * - Resto: muestra un toast con un mensaje legible, salvo `silentRequest()`.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const notifications = inject(NotificationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // `clearSession` y no `logout`: llamar al servidor aquí daría otro 401
        // y un bucle, y de todas formas el refresh token ya no sirve.
        auth.clearSession();
        router.navigate([ROUTES.login]);
      }

      if (!req.context.get(SKIP_ERROR_TOAST)) {
        notifications.error(resolveMessage(error));
      }
      return throwError(() => error);
    }),
  );
};

function resolveMessage(error: HttpErrorResponse): string {
  if (error.error?.message) return error.error.message;
  if (error.status === 0) return 'No se pudo conectar con el servidor.';
  if (error.status === 403) return 'No tienes permisos para esta acción.';
  if (error.status === 404) return 'Recurso no encontrado.';
  if (error.status >= 500) return 'Error interno del servidor.';
  return error.message || 'Ha ocurrido un error inesperado.';
}
